import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, IconButton, Grid, Stack, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Paper, 
  TablePagination, Divider, Chip, MenuItem, CircularProgress
} from "@mui/material";
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon,
  Delete as DeleteIcon, Close as CloseIcon, MeetingRoom as RoomIcon
} from "@mui/icons-material";
import { db } from "../../firebase"; 
import { 
  collection, addDoc, getDocs, query, orderBy, doc, 
  updateDoc, deleteDoc, Timestamp, where 
} from "firebase/firestore";
import Swal from "sweetalert2";

const PRIMARY = "#0d9488";

function Hostelrooms() {
  const [rooms, setRooms] = useState([]);
  const [hostels, setHostels] = useState([]); // For the Select Box
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const currentSchoolId = "zIlbJM5BjyrlEhg7fWKs";

  const [form, setForm] = useState({
    hostelId: "",
    roomNo: "",
    noOfBeds: "",
    maxStudents: "",
    status: "Available",
    schoolId: currentSchoolId
  });

  useEffect(() => {
    fetchHostels();
    fetchRooms();
  }, []);

  const fetchHostels = async () => {
    const q = query(collection(db, "hostels"), where("schoolId", "==", currentSchoolId));
    const snap = await getDocs(q);
    setHostels(snap.docs.map(doc => ({ id: doc.id, name: doc.data().hostelName })));
  };

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "hostelrooms"), 
        where("schoolId", "==", currentSchoolId),
      );
      const snap = await getDocs(q);
      setRooms(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.hostelId || !form.roomNo || !form.noOfBeds) {
      return Swal.fire("Error", "Please fill all required fields", "error");
    }
    setLoading(true);
    try {
      const payload = { ...form, updatedAt: Timestamp.now() };
      if (editId) {
        await updateDoc(doc(db, "hostelrooms", editId), payload);
        Swal.fire("Updated", "Room updated", "success");
      } else {
        await addDoc(collection(db, "hostelrooms"), payload);
        Swal.fire("Saved", "Room added", "success");
      }
      setOpen(false);
      resetForm();
      fetchRooms();
    } catch (err) { Swal.fire("Error", err.message, "error"); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Room?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33"
    });
    if (confirm.isConfirmed) {
      await deleteDoc(doc(db, "hostelrooms", id));
      fetchRooms();
    }
  };

  const resetForm = () => {
    setEditId(null);
    setForm({
      hostelId: "", roomNo: "", noOfBeds: "",
      maxStudents: "", status: "Available", schoolId: currentSchoolId
    });
  };

  const filteredData = rooms.filter(r => r.roomNo.includes(search));

  return (
    <Box p={2} sx={{ background: "#f8fafc", minHeight: "100vh" }}>
      <Stack direction="row" justifyContent="space-between" mb={3} alignItems="center">
        <Box>
            <Typography variant="h6" fontWeight="bold">Hostel Room Management</Typography>
            <Typography variant="caption" color="text.secondary">Assign beds and manage room availability</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: PRIMARY }} onClick={() => { resetForm(); setOpen(true); }}>
          Add Room
        </Button>
      </Stack>

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }} elevation={0}>
        <Box p={2}>
          <TextField 
            fullWidth size="small" placeholder="Search by Room Number..." 
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "gray" }} /> }}
          />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f1f5f9" }}>
              <TableRow>
                <TableCell><b>Sr.</b></TableCell>
                <TableCell><b>Hostel</b></TableCell>
                <TableCell><b>Room No.</b></TableCell>
                <TableCell><b>Beds / Capacity</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell align="center"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow> : 
               filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                <TableRow key={row.id} hover>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{hostels.find(h => h.id === row.hostelId)?.name || "Unknown"}</TableCell>
                  <TableCell><b>Room {row.roomNo}</b></TableCell>
                  <TableCell>{row.noOfBeds} Beds / {row.maxStudents} Students</TableCell>
                  <TableCell>
                    <Chip label={row.status} size="small" variant="outlined" color={row.status === "Available" ? "success" : "warning"} />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => { setEditId(row.id); setForm(row); setOpen(true); }} color="primary"><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(row.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={filteredData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, n) => setPage(n)} />
      </Paper>

      {/* ROOM FORM DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RoomIcon color="primary" /> {editId ? "Update Room" : "Add Room"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Grid className="row" spacing={2} mt={1}>
            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField select label="Select Hostel" fullWidth size="small" value={form.hostelId} onChange={(e) => setForm({...form, hostelId: e.target.value})}>
                {hostels.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid className="col-sm-6 mb-3" item xs={12}><TextField label="Room Number" fullWidth size="small" value={form.roomNo} onChange={(e) => setForm({...form, roomNo: e.target.value})} /></Grid>
            <Grid className="col-sm-6 mb-3" item xs={6}><TextField label="No. of Beds" type="number" fullWidth size="small" value={form.noOfBeds} onChange={(e) => setForm({...form, noOfBeds: e.target.value})} /></Grid>
            <Grid className="col-sm-6 mb-3" item xs={6}><TextField label="Max Students" type="number" fullWidth size="small" value={form.maxStudents} onChange={(e) => setForm({...form, maxStudents: e.target.value})} /></Grid>
            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField select label="Status" fullWidth size="small" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
                <MenuItem value="Available">Available</MenuItem>
                <MenuItem value="Full">Full</MenuItem>
                <MenuItem value="Under Maintenance">Under Maintenance</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Button variant="contained" fullWidth sx={{ mt: 3, bgcolor: PRIMARY }} onClick={handleSubmit} disabled={loading}>Save Room</Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Hostelrooms;